import React, { useState, useEffect, useRef, useContext } from "react";
import { Context } from "Context";
import { mapString, highlightMatches, removeHTMLTags } from "utils";
import { AccessibleText, Image, Icon, Link, Button } from "@wdpr/groot-components";
import useFetch from "hooks/useFetch";
import appConfig from "config.json";
import { getSvgBackground } from "utils/svg";

const { searchService, searchHistory, brands } = appConfig;
const { origin, pathname } = window?.location;
const searchHistoryTimeout = 3000;
const mainClass = "syndicated-navigation__chrome__search";
let cleaned = false;
let timeoutId = null;
const preventScroll = "navigation-prevent-scroll";
const minSearchQueryLength = 3;

const isSearchQueryValid = (searchQuery = "", allowedChars = 2) => {
	const trimmedSearchLength = searchQuery?.replace(/ +/g, "").length;
	return trimmedSearchLength && trimmedSearchLength >= allowedChars;
};

const generateSearchQuery = (query = "") => {
	return query?.trim().replace("...", "") || "";
};

const SearchBar = () => {
	const {
		headerData: { chrome },
		config: { basePath, searchBar, brand, position, locale },
		isMobileView,
		profileData: { userType },
		cdnAssetsUrl,
	} = useContext(Context);
	const minSearchLength =
		brands[brand]?.minSearchLength?.[locale] || minSearchQueryLength;
	const sticky = position === "sticky";
	const defaultSearchService = !brands[brand]?.searchService;
	const searchUrlParams = new URLSearchParams(window?.location.search);
	const improvedSiteSearch =
		(brands[brand]?.improvedSiteSearch && chrome?.maxResults) || false;
	const initialmaxResults = improvedSiteSearch ? chrome?.maxResults || 8 : 10;
	const [cursor, setCursor] = useState(-1);
	const [searchQuery, setSearchQuery] = useState(
		searchUrlParams.get("searchQuery") || ""
	);
	const [searchParams, setSearchParams] = useState(null);
	const [fetchTrigger, setFetchTrigger] = useState(0);
	const [searchHistoryParams, setSearchHistoryParams] = useState(null);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [filteredQuery, setFilteredQuery] = useState("");
	const [viewportHeight, setViewportHeight] = useState(window?.innerHeight);
	const [topPosition, setTopPosition] = useState(0);

	// Function to get the top position of the monitored element
	const updatePosition = () => {
		if (suggestedResult.current) {
			const rect = suggestedResult.current.getBoundingClientRect();
			setTopPosition(rect.top);
		}
	};

	useEffect(() => {
		const handleResize = () => {
			setViewportHeight(window?.visualViewport?.height || window?.innerHeight);
		};
		window?.visualViewport?.addEventListener("resize", handleResize);
		return () => {
			window?.visualViewport?.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		// Initial position update
		updatePosition();

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						updatePosition();
					}
				});
			},
			{
				threshold: 1.0, // Trigger when the entire element is visible
			}
		);

		if (suggestedResult.current) {
			observer.observe(suggestedResult.current);
		}

		// Clean up the observer on component unmount
		return () => {
			if (suggestedResult.current) {
				/* eslint-disable react-hooks/exhaustive-deps */
				observer.unobserve(suggestedResult.current);
			}
		};
	}, [showSuggestions]);

	const accessibilityTextSearchResults =
		chrome?.accessibilityTextSearchResults || "Search dialog {total} results";

	const { response, error } = useFetch(
		searchParams
			? defaultSearchService
				? `${searchService.apiUrl}?${searchParams}`
				: `${basePath}${brands[brand].searchService.apiUrl}`
			: "",
		searchParams
			? defaultSearchService
				? { method: "GET" }
				: searchParams
			: null,
		0,
		fetchTrigger
	);

	const { response: searchHistoryResponse, error: searchHistoryError } =
		useFetch(
			searchHistoryParams ? searchHistory.apiUrl : "",
			searchHistoryParams,
			0,
			searchHistoryParams
		);

	const ref = useRef(null);
	const inputRef = useRef(null);
	const suggestedResult = useRef(null);

	useEffect(() => {
		if (isMobileView) {
			inputRef.current?.focus();
		}
	}, [isMobileView]);

	const fullWordsEllipsis = (text = "") => {
		if (!defaultSearchService && text.length > 40) {
			const fitText = text.substring(0, 36);
			text = fitText.substring(0, fitText.lastIndexOf(" ")) + "...";
		}
		return text;
	};

	const extractIcon = (iconSource) => {
		return iconSource.split('<span class="icon__')[1].split(" ")[0];
	};

	const localizeLinkUrl = (linkUrl) => {
		const origin = window?.location.origin;
		if (!origin.endsWith(".eu") && !origin.endsWith(".co.uk")) {
			return linkUrl;
		}
		const url = new URL(linkUrl, origin);
		url.href = origin + url.pathname;
		return url.href;
	};

	const handleInnerClick = () => {
		if (searchQuery && response?.links?.length > 0) {
			setShowSuggestions(true);
		}
	};

	useEffect(() => {
		if (searchHistoryParams) {
			navigateToSearchResult(cursor);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchHistoryResponse, searchHistoryError]);

	useEffect(() => {
		if (showSuggestions && sticky && isMobileView) {
			document?.body.classList?.add(preventScroll);
		} else {
			document?.body.classList?.remove(preventScroll);
		}
	}, [showSuggestions, sticky, isMobileView]);

	useEffect(() => {
		if (cursor !== -1) {
			return;
		}
		setShowSuggestions(false);
		debouncedLoadResult();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery]);

	const loadResults = () => {
		const validSearchQuery = searchQuery.trim().replace(/  +/g, " ");
		if (isSearchQueryValid(searchQuery, minSearchLength)) {
			const pageName =
				pathname
					.split("/")
					.filter((part) => part.length)
					.join("_") || "homepage";
			if (defaultSearchService) {
				setSearchParams(
					mapString(
						searchService.baseParams,
						encodeURIComponent(validSearchQuery),
						pageName,
						"",
						improvedSiteSearch,
						initialmaxResults
					)
				);
			} else {
				setSearchParams({
					method: "POST",
					body: mapString(
						brands[brand].searchService.baseParams,
						encodeURIComponent(validSearchQuery),
						pageName,
						userType || "GUEST",
						improvedSiteSearch,
						initialmaxResults
					),
					headers: {
						"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
					},
				});
			}
			setFetchTrigger((fetch) => fetch + 1);
			setCursor(-1);
		} else if (validSearchQuery.length === 0) {
			setShowSuggestions(false);
		}
	};

	const debounce = (func, delay) => {
		return (...args) => {
			if (timeoutId) {
				// Clear the previous timeout
				clearTimeout(timeoutId);
			}
			// Set a new timeout
			timeoutId = setTimeout(() => {
				func(...args);
			}, delay);
		};
	};

	const debouncedLoadResult = debounce(loadResults, 300);

	useEffect(() => {
		if (
			response &&
			response.links?.length > 0 &&
			searchQuery &&
			document?.activeElement === ref.current.querySelector("input")
		) {
			setShowSuggestions(true);
		} else if (!response || response.links?.length === 0) {
			setShowSuggestions(false);
		}
		setFilteredQuery(
			generateSearchQuery(response?.filteredQuery || searchQuery)
		);
		if (
			!cleaned &&
			searchQuery &&
			searchUrlParams.get("searchQuery") === searchQuery &&
			response &&
			!response.filteredQuery
		) {
			setSearchQuery("");
			setFilteredQuery("");
			cleaned = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		setFilteredQuery(searchQuery);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!ref.current?.contains(event.target)) {
				setShowSuggestions(false);
			}
		};
		document?.addEventListener("click", handleClickOutside, true);
		return () => window?.removeEventListener("click", handleClickOutside, true);
	}, []);

	if (!chrome?.searchDisney) {
		return null;
	}

	const makeSearchHistoryRequest = (position, event) => {
		event?.preventDefault();
		setCursor(position);
		setSearchQuery(response?.links?.[position]?.title);
		setSearchHistoryParams({
			method: "PUT",
			body: JSON.stringify({
				title: response?.links?.[position]?.fullTitle,
				position,
				documentId: response?.links?.[position]?.id,
				searchId: response?.searchId,
				improved: improvedSiteSearch,
				initialmaxResults,
			}),
			headers: {
				"Content-Type": "application/json",
			},
		});
		setTimeout(() => {
			navigateToSearchResult(position);
		}, searchHistoryTimeout);
		return;
	};

	const search = () => {
		if (isSearchQueryValid(searchQuery, minSearchLength)) {
			window?.location.assign(
				mapString(
					chrome?.searchPage ||
						`${origin}${basePath}/search/?searchQuery={query}`,
					encodeURIComponent(searchQuery)
				)
			);
		}
	};

	const navigateToSearchResult = (cursorPosition) => {
		if (cursorPosition === -1) {
			return;
		}
		const searchUrl = response?.links?.[cursorPosition]?.url;
		if (!searchUrl || searchUrl === "" || searchUrl === "/#") {
			search();
		} else {
			window?.location.assign(localizeLinkUrl(searchUrl));
		}
	};

	const keyboardNavigation = (e) => {
		let currentCursor = cursor;

		if (e.key === "ArrowDown" || e.key === "ArrowUp") {
			currentCursor = cursor > 0 ? cursor - 1 : 0;
			if (e.key === "ArrowDown") {
				currentCursor =
					cursor < response?.links?.length - 1 ? cursor + 1 : cursor;
			}
			setCursor(currentCursor);
			setSearchQuery(response?.links?.[currentCursor]?.title);
		}

		if (e.key === "Enter") {
			if (currentCursor === -1) {
				search();
			} else if (currentCursor >= 0) {
				makeSearchHistoryRequest(currentCursor);
			}
		}
		if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter") {
			setCursor(-1);
		}

		if (e.key === "Escape") {
			setShowSuggestions(false);
		}
	};

	return searchBar ? (
		<div
			role="search"
			ref={ref}
			className={`${mainClass} ${mainClass}--${brand}`}
		>
			<div
				className={
					`${mainClass}__form ${mainClass}__form--${brand}` +
					(showSuggestions ? ` ${mainClass}__form--focus` : "")
				}
			>
				<Button
					accessibleText={chrome?.confirmSearch}
					className={`${mainClass}__form__button syndicated-icon syndicated-icon--search${
						filteredQuery ? "" : " syndicated-icon--search--disabled"
					}`}
					customStyles
					onClick={search}
					style={getSvgBackground("search")}
				/>
				<input
					ref={inputRef}
					aria-label={chrome?.searchDisney}
					onClick={handleInnerClick}
					className={`${mainClass}__form__input ${mainClass}__form__input--${brand}${
						searchQuery ? "" : ` ${mainClass}__form__input--empty`
					}`}
					type="search"
					value={searchQuery}
					onChange={(event) => {
						setSearchQuery(removeHTMLTags(event.target.value));
						setFilteredQuery("");
					}}
					placeholder={chrome?.searchDisney}
					maxLength="254"
					autoComplete="off"
					name="searchQuery"
					onKeyDown={(e) => keyboardNavigation(e)}
					role="combobox"
					aria-expanded={showSuggestions ? "true" : "false"}
					aria-controls={`${mainClass}__autosuggest`}
					aria-autocomplete="list"
				/>
				{searchQuery && (
					<Button
						accessibleText={chrome?.clearSearch}
						className={`${mainClass}__form__button syndicated-icon syndicated-icon--close-button`}
						customStyles
						onClick={() => setSearchQuery("")}
						style={{
							backgroundImage: `url(${cdnAssetsUrl}/icons/pep/close-button.svg)`,
						}}
					/>
				)}
			</div>
			<AccessibleText ariaLive="assertive">
				{searchQuery &&
					showSuggestions &&
					mapString(
						accessibilityTextSearchResults,
						response?.links?.length || 0
					)}
			</AccessibleText>
			{showSuggestions && searchQuery && (
				<ul
					className={`${mainClass}__autosuggest ${mainClass}__autosuggest--${brand} ${mainClass}__autosuggest--${
						chrome?.allResultText && improvedSiteSearch ? `no-border` : ``
					}`}
					style={{
						maxHeight: sticky
							? `calc(${viewportHeight}px - ${topPosition}px)`
							: "auto",
						overflowY: "auto",
					}}
					role="listbox"
					id={`${mainClass}__autosuggest`}
					aria-labelledby={`${mainClass}__autosuggest__list-item--${chrome?.searchResults}`}
					ref={suggestedResult}
				>
					<li
						className={`${mainClass}__autosuggest__list-item ${mainClass}__autosuggest__list-item--${brand}`}
					>
						{chrome?.searchResults}
					</li>
					{response?.links?.map((link, index) => (
						<li
							key={link.id}
							className={`${mainClass}__autosuggest__list-item ${mainClass}__autosuggest__list-item--${brand} ${
								cursor >= 0 && cursor === index
									? `${mainClass}__autosuggest__list-item--selected`
									: ""
							} ${
								improvedSiteSearch
									? `${mainClass}__autosuggest__list-item--improved`
									: ""
							}`}
							role="presentation"
							id={`${mainClass}__autosuggest__list-item--${index}`}
						>
							<Link
								href={localizeLinkUrl(link.url)}
								analyticsLinkId={link.analyticsLinkId}
								handleClick={(event) => makeSearchHistoryRequest(index, event)}
							>
								{improvedSiteSearch ? (
									<span className="syndicated-image-container">
										{link.mediaUrl ||
										chrome?.suggestedResultsfallbackImage?.url ? (
											<Image
												image={{
													id: link?.analyticsLinkId,
													url:
														link.mediaUrl ||
														chrome?.suggestedResultsfallbackImage?.url,
													alt: !link.mediaUrl
														? chrome?.suggestedResultsFallbackImageAltText
														: "",
													width: 50,
													height: 50,
												}}
											/>
										) : (
											<div className="syndicated-image-fallback"></div>
										)}
									</span>
								) : (
									<span className="syndicated-icon-container">
										<Icon
											icon={
												defaultSearchService
													? link.icon
													: extractIcon(link.webFontCode)
											}
											cdnUrl={cdnAssetsUrl}
										/>
									</span>
								)}
								<div className="syndicated-link__details">
									<div className="syndicated-link__details__title">
										<quicklink
											dangerouslySetInnerHTML={{
												__html:
													link.emphasizedTitle ||
													link.emphasizedFaqQuestion ||
													highlightMatches(
														link.title || link.desc,
														encodeURIComponent(filteredQuery)
													),
											}}
										></quicklink>
									</div>
									{improvedSiteSearch ? (
										<div
											className="syndicated-link__details__description"
											dangerouslySetInnerHTML={{
												__html: link.recordType,
											}}
										/>
									) : (
										<div
											className="syndicated-link__details__description"
											dangerouslySetInnerHTML={{
												__html: link.title
													? fullWordsEllipsis(link.desc)
													: "&nbsp;",
											}}
										/>
									)}
								</div>
							</Link>
						</li>
					))}
					{chrome?.allResultText && improvedSiteSearch && (
						<li
							id={`${mainClass}__autosuggest__list-item--all-results`}
							className={`${mainClass}__autosuggest__list-item ${mainClass}__autosuggest__list-item--${brand}
							${mainClass}__autosuggest__list-item--all-results`}
							onClick={search}
						>
							{chrome?.allResultText}
						</li>
					)}
				</ul>
			)}
		</div>
	) : null;
};
export default SearchBar;
